import React from 'react';
import { useSiteImages } from '../context/SiteImagesContext';
import { RoiCalculator } from '../components/RoiCalculator';
import { PageHeaderBanner } from '../components/PageHeaderBanner';
import { CheckCircle2, TrendingUp, ShieldCheck, HelpCircle } from 'lucide-react';

interface CalculatorPageProps {
  onNavigate: (page: string) => void;
}

export const CalculatorPage: React.FC<CalculatorPageProps> = ({ onNavigate }) => {
  const { images } = useSiteImages();
  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Page Title Header with Duotone Background (matching example) */}
      <PageHeaderBanner
        breadcrumb="ROI Calculator"
        parentPage={{ label: 'Home', page: 'home' }}
        badgeIcon={<TrendingUp className="w-3.5 h-3.5" />}
        badgeText="Entrepreneur Financial Modeling & Cashflow"
        badgeColor="emerald"
        title="Commercial Vending Profit & Payback Calculator"
        description="Estimate daily cashflow, gross profit margins, and machine amortization for Milk ATMs, Water Refill Kiosks, and Cooking Oil ATMs across Kenyan trading locations."
        backgroundImage={images.calculatorBanner}
        gradientTheme="emerald-amber"
        onNavigate={onNavigate}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">

      {/* Main Interactive Tool */}
      <RoiCalculator />

      {/* Practical Guide to Starting a Vending Business in Kenya */}
      <div className="bg-slate-100 rounded-3xl p-8 sm:p-12 border border-slate-200 space-y-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-sky-700">
            Startup Blueprint
          </span>
          <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mt-1">
            4 Key Milestones to Launching a Profitable Vending Business
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="w-8 h-8 rounded-lg bg-sky-100 text-sky-700 font-bold text-xs flex items-center justify-center">
              1
            </div>
            <h3 className="text-sm font-bold text-slate-900">Site Selection & Footfall</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Identify high-density residential hubs, bus stages, estates, or shopping centers where families purchase daily milk and drinking water.
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center">
              2
            </div>
            <h3 className="text-sm font-bold text-slate-900">Reliable Supply Sourcing</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Partner with registered dairy cooperatives for pasteurized milk (or invest in a Saints Tech 300L Pasteurizer to buy raw milk directly from farmers).
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 font-bold text-xs flex items-center justify-center">
              3
            </div>
            <h3 className="text-sm font-bold text-slate-900">Health & County Licenses</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Obtain the County Single Business Permit and Public Health Food Handler's medical certificate. Our machines meet all public health sanitary standards.
            </p>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 font-bold text-xs flex items-center justify-center">
              4
            </div>
            <h3 className="text-sm font-bold text-slate-900">Machine Setup & Training</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Saints Tech Solutions delivers, installs, and provides full staff training on calibration, daily cleaning cycles (CIP), and GSM SMS sales audit tracking.
            </p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h4 className="text-sm font-bold text-slate-900">
              Need assistance selecting the right machine capacity for your budget?
            </h4>
            <p className="text-xs text-slate-500">
              Our business advisory team in Nairobi can review your target location and recommend suitable systems.
            </p>
          </div>
          <button
            onClick={() => onNavigate('contact')}
            className="px-5 py-2.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shrink-0"
          >
            Schedule Consultation
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
